import { z } from "zod";

import {
  fileType,
  fileBillType,
  fileClientType,
  fileStatus,
  fileClientCategory,
} from "../../../constant/enum";
import { IBranch } from "../../../general/type/branch.types";
import { IUserResponse } from "../../user/type/user";

const BaseCommonSchema = z.object({
  refNumber: z
    .string()
    .min(3, "Reference number must be more than 3 characters"),
  clientType: z.enum(fileClientType as [string, ...string[]]),
  fileRemark: z.string().optional(),
  fileType: z.enum(fileType as [string, ...string[]]),
  picName: z.string().optional(),
  completionDate: z.date().optional(),
  clientId: z
    .string()
    .regex(/^[0-9a-fA-F]{24}$/, "Invalid client ID")
    .optional(),
  purchaserId: z
    .string()
    .regex(/^[0-9a-fA-F]{24}$/, "Invalid purchaser ID")
    .optional(),
  vendorId: z
    .string()
    .regex(/^[0-9a-fA-F]{24}$/, "Invalid vendor ID")
    .optional(),
  branchId: z.string().regex(/^[0-9a-fA-F]{24}$/, "Invalid branch ID"),
  clientCategoryRemark: z.string().optional(),
  fileTypeRemark: z.string().optional(),
  billTypeRemark: z.string().optional(),
  fileProfit: z
    .object({
      fileRevenue: z.number().optional(),
      disbursement: z.number().optional(),
      agentCommission: z.number().optional(),
      downpayment: z.number().optional(),
      netProfit: z.number().optional(),
    })
    .optional(),
});

// Schema for litigation file type
const LitigationFileSchema = BaseCommonSchema.extend({
  fileType: z.literal("litigation"),
  fileRemark: z.string(),
  clientId: z.string().regex(/^[0-9a-fA-F]{24}$/, "Invalid client ID"),
  // These fields are not required for litigation
  billType: z.enum(fileBillType as [string, ...string[]]).optional(),
  propertyAddress: z.string().optional(),
  spaPrice: z.number().optional(),
  agentId: z
    .string()
    .regex(/^[0-9a-fA-F]{24}$/, "Invalid agent ID")
    .optional(),
  clientCategory: z
    .enum(fileClientCategory as [string, ...string[]])
    .optional(),
});

// Create a non-litigation file type enum by filtering out "litigation"
const nonLitigationFileTypes = fileType.filter(
  (type) => type !== "litigation"
) as [string, ...string[]];

// Schema for non-litigation file types
const NonLitigationFileSchema = BaseCommonSchema.extend({
  // Override fileType with more specific type
  clientCategory: z.enum(fileClientCategory as [string, ...string[]]),
  fileType: z.enum(nonLitigationFileTypes),
  billType: z.enum(fileBillType as [string, ...string[]]),
  propertyAddress: z
    .string()
    .min(3, "Property address must be more than 3 characters"),
  spaPrice: z
    .number()
    .min(1, "SPA/Loan/Finance/Tenancy/Contract Amount must be more than 0"),
  agentId: z.string().regex(/^[0-9a-fA-F]{24}$/, "Invalid agent ID"),
});

// Create a schema for initial validation that just checks if required fields are present
const InitialValidationSchema = z.object({
  refNumber: z.string(),
  clientType: z.string(),
  fileType: z.string(),
  billType: z.string(),
  propertyAddress: z.string(),
  spaPrice: z.number(),
  branchId: z.string(),
  clientId: z.string(),
  agentId: z.string(),
});

// Combined schema that first validates required fields, then applies the discriminated union
export const FileRequestSchema = z.preprocess(
  (data) => data,
  z.union([
    // First try the initial validation to check required fields
    InitialValidationSchema.and(
      // Then apply the discriminated union for specific validation
      z.discriminatedUnion("fileType", [
        LitigationFileSchema,
        NonLitigationFileSchema,
      ])
    ),
    // If initial validation fails, fall back to the discriminated union
    z.discriminatedUnion("fileType", [
      LitigationFileSchema,
      NonLitigationFileSchema,
    ]),
  ])
);

// Schema for file responses from API
export const FileResponseSchema = BaseCommonSchema.extend({
  id: z.string(),
  billType: z.enum(fileBillType as [string, ...string[]]).optional(),
  propertyAddress: z.string().optional(),
  spaPrice: z.number().optional(),
  user: z.custom<IUserResponse>().optional(),
  agent: z.custom<IUserResponse>().optional(),
  client: z.custom<IUserResponse>().optional(),
  purchaser: z.custom<IUserResponse>().optional(),
  vendor: z.custom<IUserResponse>().optional(),
  branch: z.custom<IBranch>().optional(),
  clientCategory: z
    .enum(fileClientCategory as [string, ...string[]])
    .optional(),
  statusHistory: z.array(z.custom<IStatusHistory>()).optional(),
  currentStatus: z.custom<IStatusHistory>().optional(),
  completionDate: z.date().optional(),
  completedDate: z.date().optional(),
  picName: z.string().optional(),
  archive: z.date().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export const FileStatusRequestSchema = z.object({
  status: z.enum(fileStatus as [string, ...string[]]),
  statusRemark: z.string(),
  dateExecuted: z.date().optional(),
});

export const FileProfitRequestSchema = z.object({
  fileRevenue: z.number().optional(),
  disbursement: z.number().optional(),
  agentCommission: z.number().optional(),
  downpayment: z.number().optional(),
});

export type IFileRequest = z.infer<typeof FileRequestSchema>;
export type IFileStatusRequest = z.infer<typeof FileStatusRequestSchema>;
export type IFileResponse = z.infer<typeof FileResponseSchema>;
export type IFileProfitRequest = z.infer<typeof FileProfitRequestSchema>;

export interface IApiResponseGetFiles {
  results: IFileResponse[];
  page: string;
  limit: number;
  totalPages: number;
  totalResults: number;
}

interface IStatusHistory {
  status: string;
  statusRemark: string;
  dateExecuted: string;
  fileURL?: string;
  createdAt: string;
  updatedAt: string;
}
